<template>
  <section>
    <h2>準則庫</h2>
    <b-button
      variant="outline-secondary"
      class="mb-3"
      :disabled="isLoadingCriteria"
      @click="loadCriteria"
    >
      <b-icon icon="arrow-clockwise" aria-hidden="true" />
      {{ isLoadingCriteria ? "讀取中..." : "讀取準則列表" }}
    </b-button>

    <b-alert :show="Boolean(detailError)" variant="danger">
      {{ detailError }}
    </b-alert>

    <b-alert :show="Boolean(criteriaError)" variant="danger">
      {{ criteriaError }}
    </b-alert>

    <b-alert :show="Boolean(deleteError)" variant="danger">
      {{ deleteError }}
    </b-alert>

    <b-table :items="criteria" :fields="criteriaFields" responsive class="mb-4">
      <template #cell(actions)="data">
        <b-button
          variant="outline-secondary"
          :disabled="isLoadingDetail"
          title="查看準則"
          aria-label="查看準則"
          @click="loadCriterion(data.item.id)"
        >
          <b-icon icon="eye" aria-hidden="true" />
        </b-button>
        <b-button
          variant="outline-secondary"
          class="ml-2"
          title="修改名稱與備註"
          aria-label="修改名稱與備註"
          @click="openCriterionEdit(data.item)"
        >
          <b-icon icon="pencil" aria-hidden="true" />
        </b-button>

        <b-button
          variant="outline-danger"
          class="ml-2"
          :disabled="isDeletingCriterion"
          title="刪除準則"
          aria-label="刪除準則"
          @click="deleteCriterion(data.item)"
        >
          <b-icon icon="trash" aria-hidden="true" />
        </b-button>
      </template>
    </b-table>
    <b-button
      variant="outline-secondary"
      class="mb-3"
      @click="downloadTemplate"
    >
      <b-icon icon="download" aria-hidden="true" />
      下載題目範本
    </b-button>

    <b-form-group label="匯入題目" label-for="qa-question-file">
      <b-form-file
        id="qa-question-file"
        v-model="selectedFile"
        accept=".xlsx,.xls,.csv"
        placeholder="選擇檔案"
        drop-placeholder="放開以選取檔案"
        browse-text="選擇檔案"
        :disabled="isParsing"
        @input="readFile"
      />
    </b-form-group>

    <p v-if="selectedFile">已選擇：{{ selectedFile.name }}</p>

    <p v-if="isParsing">讀取中...</p>

    <b-alert
      :show="Boolean(errorMessage)"
      variant="danger"
      class="import-error"
    >
      {{ errorMessage }}
    </b-alert>

    <div v-if="items.length">
      <h3>題目預覽</h3>

      <b-table :items="items" :fields="fields" responsive />
    </div>

    <div v-if="items.length">
      <h3>準則設定</h3>

      <b-form-group label="準則庫名稱" label-for="qa-criterion-name">
        <b-form-input
          id="qa-criterion-name"
          v-model.trim="criterionName"
          maxlength="128"
        />
      </b-form-group>

      <b-form-group label="備註" label-for="qa-criterion-note">
        <b-form-textarea id="qa-criterion-note" v-model="note" rows="3" />
      </b-form-group>

      <b-form-group label="編碼方式">
        <b-form-radio-group v-model="codingType" name="qa-coding-type">
          <b-form-radio value="scale">N 點量表</b-form-radio>
          <b-form-radio value="score">分數</b-form-radio>
          <b-form-radio value="label">標籤</b-form-radio>
        </b-form-radio-group>
      </b-form-group>
      <div v-if="codingType === 'scale' || codingType === 'score'">
        <b-form-group label="最小值" label-for="qa-min">
          <b-form-input
            id="qa-min"
            v-model.number="scale.min"
            type="number"
            :step="codingType === 'scale' ? 1 : 'any'"
          />
        </b-form-group>

        <b-form-group label="最大值" label-for="qa-max">
          <b-form-input
            id="qa-max"
            v-model.number="scale.max"
            type="number"
            :step="codingType === 'scale' ? 1 : 'any'"
          />
        </b-form-group>

        <b-form-group label="最小值定義" label-for="qa-min-definition">
          <b-form-textarea
            id="qa-min-definition"
            v-model.trim="scale.minDef"
            rows="2"
          />
        </b-form-group>

        <b-form-group label="最大值定義" label-for="qa-max-definition">
          <b-form-textarea
            id="qa-max-definition"
            v-model.trim="scale.maxDef"
            rows="2"
          />
        </b-form-group>

        <div v-if="codingType === 'scale' && middlePoints.length">
          <b-form-group
            v-for="point in middlePoints"
            :key="point"
            :label="point + ' 分定義（選填）'"
            :label-for="'qa-point-' + point"
          >
            <b-form-input
              :id="'qa-point-' + point"
              :value="scale.pointDefs[String(point)] || ''"
              @input="setPointDefinition(point, $event)"
            />
          </b-form-group>
        </div>

        <b-alert :show="Boolean(rangeError)" variant="danger">
          {{ rangeError }}
        </b-alert>
      </div>
      <div v-if="codingType === 'label'">
        <div
          v-for="(label, index) in labels"
          :key="index"
          class="d-flex align-items-start mb-3"
        >
          <b-form-group
            :label="'標籤 ' + label.code + ' 說明'"
            :label-for="'qa-label-' + label.code"
            class="flex-grow-1 mb-0"
          >
            <b-form-input
              :id="'qa-label-' + label.code"
              v-model.trim="label.description"
            />
          </b-form-group>

          <b-button
            variant="outline-danger"
            class="ml-2"
            :disabled="labels.length <= 1"
            :aria-label="'刪除標籤 ' + label.code"
            :title="'刪除標籤 ' + label.code"
            @click="removeLabel(index)"
          >
            <b-icon icon="trash" aria-hidden="true" />
          </b-button>
        </div>

        <b-button
          variant="outline-secondary"
          :disabled="labels.length >= 26"
          @click="addLabel"
        >
          <b-icon icon="plus" aria-hidden="true" />
          新增標籤
        </b-button>
        <b-alert :show="Boolean(labelError)" variant="danger" class="mt-3">
          {{ labelError }}
        </b-alert>
      </div>
      <b-alert :show="Boolean(criterionError)" variant="warning" class="mt-3">
        {{ criterionError }}
      </b-alert>

      <b-button
        variant="primary"
        :disabled="isSaving || Boolean(criterionError)"
        @click="saveCriterion"
      >
        <b-icon icon="save" aria-hidden="true" />
        {{ isSaving ? "儲存中..." : "儲存準則庫" }}
      </b-button>

      <b-alert :show="Boolean(saveError)" variant="danger" class="mt-3">
        {{ saveError }}
      </b-alert>

      <b-alert :show="Boolean(saveSuccess)" variant="success" class="mt-3">
        {{ saveSuccess }}
      </b-alert>
    </div>

    <b-modal
      ref="criterionDetailModal"
      title="準則詳細資料"
      size="lg"
      ok-only
      ok-title="關閉"
    >
      <section v-if="selectedCriterion" class="mb-4">
        <h3>{{ selectedCriterion.name }}</h3>
        <b-button
          variant="outline-primary"
          class="mb-3"
          :disabled="isSaving || isParsing"
          @click="copyCriterionToNewDraft"
        >
          <b-icon icon="files" aria-hidden="true" />
          複製為新準則
        </b-button>
        <p>備註：{{ selectedCriterion.note || "無" }}</p>
        <p>編碼方式：{{ selectedCriterion.codingType }}</p>

        <div v-if="selectedCriterion.codingType === 'label'">
          <dl>
            <div v-for="label in selectedCriterion.labels" :key="label.code">
              <dt>{{ label.code }}</dt>
              <dd>{{ label.description }}</dd>
            </div>
          </dl>
        </div>

        <div v-else-if="selectedCriterion.scale">
          <p>
            範圍：{{ selectedCriterion.scale.min }}–
            {{ selectedCriterion.scale.max }}
          </p>
          <p>最小值定義：{{ selectedCriterion.scale.minDef }}</p>
          <p>最大值定義：{{ selectedCriterion.scale.maxDef }}</p>

          <dl v-if="selectedCriterion.codingType === 'scale'">
            <div
              v-for="(definition, point) in selectedCriterion.scale.pointDefs"
              :key="point"
            >
              <dt>{{ point }} 分</dt>
              <dd>{{ definition }}</dd>
            </div>
          </dl>
        </div>

        <b-table :items="selectedCriterion.items" :fields="fields" responsive />
      </section>
    </b-modal>

    <b-modal
      ref="criterionEditModal"
      title="修改準則"
      ok-title="儲存"
      cancel-title="取消"
      :ok-disabled="isUpdatingCriterion || !editName.trim()"
      :cancel-disabled="isUpdatingCriterion"
      :no-close-on-backdrop="isUpdatingCriterion"
      :no-close-on-esc="isUpdatingCriterion"
      :hide-header-close="isUpdatingCriterion"
      @ok.prevent="saveCriterionEdit"
    >
      <b-form-group label="名稱" label-for="qa-edit-name">
        <b-form-input
          id="qa-edit-name"
          v-model.trim="editName"
          maxlength="128"
          :disabled="isUpdatingCriterion"
        />
      </b-form-group>

      <b-form-group label="備註" label-for="qa-edit-note">
        <b-form-textarea
          id="qa-edit-note"
          v-model="editNote"
          rows="3"
          :disabled="isUpdatingCriterion"
        />
      </b-form-group>

      <div v-if="editingCriterion">
        <div v-if="editingCriterion.codingType === 'label'">
          <b-form-group
            v-for="label in editingCriterion.labels"
            :key="label.code"
            :label="'標籤 ' + label.code + ' 說明'"
            :label-for="'qa-edit-label-' + label.code"
          >
            <b-form-input
              :id="'qa-edit-label-' + label.code"
              v-model.trim="label.description"
              :disabled="isUpdatingCriterion"
            />
          </b-form-group>
        </div>

        <div v-else>
          <p>
            範圍：{{ editingCriterion.scale.min }}–
            {{ editingCriterion.scale.max }}
          </p>

          <b-form-group label="最小值定義" label-for="qa-edit-min-def">
            <b-form-textarea
              id="qa-edit-min-def"
              v-model.trim="editingCriterion.scale.minDef"
              :disabled="isUpdatingCriterion"
              rows="2"
            />
          </b-form-group>

          <b-form-group label="最大值定義" label-for="qa-edit-max-def">
            <b-form-textarea
              id="qa-edit-max-def"
              v-model.trim="editingCriterion.scale.maxDef"
              :disabled="isUpdatingCriterion"
              rows="2"
            />
          </b-form-group>

          <div v-if="editingCriterion.codingType === 'scale'">
            <b-form-group label="補充定義的分數" label-for="qa-edit-new-point">
              <b-input-group>
                <b-form-input
                  id="qa-edit-new-point"
                  v-model.number="editPoint"
                  type="number"
                  step="1"
                  :min="editingCriterion.scale.min + 1"
                  :max="editingCriterion.scale.max - 1"
                  :disabled="isUpdatingCriterion"
                />
                <b-input-group-append>
                  <b-button
                    :disabled="isUpdatingCriterion"
                    title="新增分數定義欄位"
                    aria-label="新增分數定義欄位"
                    @click="addEditPointDefinition"
                  >
                    <b-icon icon="plus" aria-hidden="true" />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
            <b-form-group
              v-for="(definition, point) in editingCriterion.scale.pointDefs"
              :key="point"
              :label="point + ' 分定義（選填）'"
              :label-for="'qa-edit-point-' + point"
            >
              <b-form-input
                :id="'qa-edit-point-' + point"
                v-model.trim="editingCriterion.scale.pointDefs[point]"
                :disabled="isUpdatingCriterion"
              />
            </b-form-group>
          </div>
        </div>
      </div>

      <b-alert :show="Boolean(editError)" variant="danger">
        {{ editError }}
      </b-alert>
    </b-modal>
  </section>
</template>

<script>
import axios from "axios";
import { parseQuestionFile, downloadQuestionTemplate } from "@/utils/qaExcel";

export default {
  name: "QACriterionEditor",

  data() {
    return {
      selectedFile: null,
      items: [],
      errorMessage: "",
      isParsing: false,
      fields: [
        { key: "no", label: "編號", thStyle: { whiteSpace: "nowrap" } },
        { key: "text", label: "對話文本" },
      ],
      criterionName: "",
      note: "",
      codingType: "scale",
      scale: {
        min: 1,
        max: 5,
        minDef: "",
        maxDef: "",
        pointDefs: {},
      },
      labels: [{ code: "A", description: "" }],
      isSaving: false,
      saveError: "",
      saveSuccess: "",
      criteria: [],
      isLoadingCriteria: false,
      criteriaError: "",
      criteriaFields: [
        { key: "id", label: "ID" },
        { key: "name", label: "名稱" },
        { key: "codingType", label: "編碼方式" },
        { key: "itemCount", label: "題數" },
        { key: "actions", label: "操作" },
      ],
      selectedCriterion: null,
      isLoadingDetail: false,
      detailError: "",
      editCriterionId: null,
      editName: "",
      editNote: "",
      isUpdatingCriterion: false,
      editError: "",
      isDeletingCriterion: false,
      deleteError: "",
      editingCriterion: null,
      editPoint: null,
      isLoadingEdit: false,
    };
  },

  methods: {
    async readFile(file) {
      this.items = [];
      this.errorMessage = "";

      if (!file) return;

      this.isParsing = true;

      try {
        this.items = await parseQuestionFile(file);
      } catch (error) {
        this.errorMessage = error.message || "無法讀取檔案";
      } finally {
        this.isParsing = false;
      }
    },
    downloadTemplate() {
      downloadQuestionTemplate();
    },
    addLabel() {
      if (this.labels.length >= 26) return;

      const code = String.fromCharCode(65 + this.labels.length);

      this.labels.push({ code, description: "" });
    },
    removeLabel(index) {
      this.labels.splice(index, 1);

      this.labels.forEach((label, labelIndex) => {
        label.code = String.fromCharCode(65 + labelIndex);
      });
    },
    setPointDefinition(point, value) {
      this.$set(this.scale.pointDefs, String(point), value);
    },
    addEditPointDefinition() {
      if (
        this.isUpdatingCriterion ||
        this.editingCriterion?.codingType !== "scale"
      )
        return;
      const point = this.editPoint;
      const scale = this.editingCriterion.scale;

      if (
        !Number.isSafeInteger(point) ||
        point <= scale.min ||
        point >= scale.max
      ) {
        this.editError = "請輸入介於最小值與最大值之間的整數";
        return;
      }
      const key = String(point);
      if (Object.prototype.hasOwnProperty.call(scale.pointDefs, key)) {
        this.editError = "此分數已有定義欄位，請直接修改下方內容";
        return;
      }
      this.$set(scale.pointDefs, key, "");
      this.editPoint = null;
      this.editError = "";
    },

    async saveCriterion() {
      if (this.isSaving || this.criterionError) return;

      this.saveError = "";
      this.saveSuccess = "";
      this.isSaving = true;

      try {
        const apiBase = process.env.VUE_APP_API_BASE;
        if (!apiBase) throw new Error("尚未設定 API 網址");

        const content = {
          codingType: this.codingType,
          items: this.items,
        };

        if (this.codingType !== "label") {
          const pointDefs = Object.fromEntries(
            Object.entries(this.scale.pointDefs).filter(
              ([key, value]) =>
                this.codingType === "scale" &&
                Number(key) >= this.scale.min &&
                Number(key) <= this.scale.max &&
                value.trim() !== "",
            ),
          );

          content.scale = { ...this.scale, pointDefs };
        }

        const response = await axios.post(
          `${apiBase.replace(/\/+$/, "")}/api/qa/criteria`,
          {
            name: this.criterionName.trim(),
            note: this.note,
            content,
            labelDefs: this.codingType === "label" ? this.labels : [],
          },
        );

        this.saveSuccess = `準則庫已儲存，ID：${response.data.id}`;
        await this.loadCriteria();
      } catch (error) {
        this.saveError =
          error.response?.data?.message || error.message || "儲存失敗";
      } finally {
        this.isSaving = false;
      }
    },
    async loadCriteria() {
      if (this.isLoadingCriteria) return;

      this.isLoadingCriteria = true;
      this.criteriaError = "";

      try {
        const apiBase = process.env.VUE_APP_API_BASE;
        if (!apiBase) throw new Error("尚未設定 API 網址");

        const response = await axios.get(
          `${apiBase.replace(/\/+$/, "")}/api/qa/criteria`,
        );

        this.criteria = response.data;
      } catch (error) {
        this.criteriaError =
          error.response?.data?.message || error.message || "讀取列表失敗";
      } finally {
        this.isLoadingCriteria = false;
      }
    },
    async loadCriterion(id) {
      if (this.isLoadingDetail) return;

      this.isLoadingDetail = true;
      this.detailError = "";
      this.selectedCriterion = null;

      try {
        const apiBase = process.env.VUE_APP_API_BASE;
        if (!apiBase) throw new Error("尚未設定 API 網址");

        const response = await axios.get(
          `${apiBase.replace(/\/+$/, "")}/api/qa/criteria/${id}`,
        );

        this.selectedCriterion = response.data;
        await this.$nextTick();
        this.$refs.criterionDetailModal.show();
      } catch (error) {
        this.detailError =
          error.response?.data?.message || error.message || "讀取準則失敗";
      } finally {
        this.isLoadingDetail = false;
      }
    },
    async openCriterionEdit(criterion) {
      if (this.isLoadingEdit || this.isUpdatingCriterion) return;

      this.isLoadingEdit = true;
      this.editError = "";
      this.criteriaError = "";
      this.editingCriterion = null;

      try {
        const apiBase = process.env.VUE_APP_API_BASE;
        if (!apiBase) throw new Error("尚未設定 API 網址");

        const response = await axios.get(
          `${apiBase.replace(/\/+$/, "")}/api/qa/criteria/${criterion.id}`,
        );

        this.editingCriterion = response.data;
        this.editPoint = null;
        this.editCriterionId = response.data.id;
        this.editName = response.data.name;
        this.editNote = response.data.note ?? "";

        await this.$nextTick();
        this.$refs.criterionEditModal.show();
      } catch (error) {
        this.criteriaError =
          error.response?.data?.message || error.message || "讀取失敗";
      } finally {
        this.isLoadingEdit = false;
      }
    },
    async saveCriterionEdit() {
      if (this.isUpdatingCriterion || !this.editingCriterion) return;

      this.editError = "";

      if (!this.editName.trim() || this.editName.trim().length > 128) {
        this.editError = "名稱必填，且不可超過 128 個字元";
        return;
      }

      this.isUpdatingCriterion = true;

      try {
        const apiBase = process.env.VUE_APP_API_BASE;
        if (!apiBase) throw new Error("尚未設定 API 網址");

        const criterion = this.editingCriterion;

        const definitions =
          criterion.codingType === "label"
            ? criterion.labels.map((label) => ({
                code: label.code,
                description: label.description,
              }))
            : {
                minDef: criterion.scale.minDef,
                maxDef: criterion.scale.maxDef,
                ...(criterion.codingType === "scale"
                  ? { pointDefs: criterion.scale.pointDefs }
                  : {}),
              };

        await axios.patch(
          `${apiBase.replace(/\/+$/, "")}/api/qa/criteria/${
            this.editCriterionId
          }`,
          {
            name: this.editName.trim(),
            note: this.editNote,
            definitions,
          },
        );

        this.$refs.criterionEditModal.hide();
        this.selectedCriterion = null;
        await this.loadCriteria();
      } catch (error) {
        this.editError =
          error.response?.data?.message || error.message || "修改失敗";
      } finally {
        this.isUpdatingCriterion = false;
      }
    },
    async deleteCriterion(criterion) {
      if (this.isDeletingCriterion) return;

      this.deleteError = "";
      this.isDeletingCriterion = true;

      try {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          `確定刪除「${criterion.name}」嗎？刪除後無法復原。`,
          {
            title: "刪除準則庫",
            okTitle: "刪除",
            cancelTitle: "取消",
            okVariant: "danger",
            centered: true,
          },
        );

        if (!confirmed) return;

        const apiBase = process.env.VUE_APP_API_BASE;
        if (!apiBase) throw new Error("尚未設定 API 網址");

        await axios.delete(
          `${apiBase.replace(/\/+$/, "")}/api/qa/criteria/${criterion.id}`,
        );

        if (this.selectedCriterion?.id === criterion.id) {
          this.selectedCriterion = null;
        }

        await this.loadCriteria();
      } catch (error) {
        this.deleteError =
          error.response?.data?.message || error.message || "刪除失敗";
      } finally {
        this.isDeletingCriterion = false;
      }
    },
    async copyCriterionToNewDraft() {
      if (!this.selectedCriterion || this.isSaving || this.isParsing) return;

      const source = this.selectedCriterion;

      // 清空選檔後，等待選檔事件完成，再填入副本題目。
      this.selectedFile = null;
      await this.$nextTick();

      this.criterionName = `${source.name}（副本）`.slice(0, 128);
      this.note = source.note ?? "";
      this.codingType = source.codingType;
      this.items = source.items.map((item) => ({ ...item }));
      this.labels = source.labels.map((label) => ({ ...label }));

      this.scale = source.scale
        ? {
            ...source.scale,
            pointDefs: { ...source.scale.pointDefs },
          }
        : {
            min: 1,
            max: 5,
            minDef: "",
            maxDef: "",
            pointDefs: {},
          };

      this.errorMessage = "";
      this.saveError = "";
      this.saveSuccess = "";

      this.$refs.criterionDetailModal.hide();
    },
  },

  computed: {
    rangeError() {
      if (this.codingType === "label") return "";

      const { min, max } = this.scale;

      if (!Number.isFinite(min) || !Number.isFinite(max)) {
        return "請填寫有效的最小值與最大值";
      }

      if (
        this.codingType === "scale" &&
        (!Number.isInteger(min) || !Number.isInteger(max))
      ) {
        return "量表的最小值與最大值必須是整數";
      }

      if (min >= max) {
        return "最小值必須小於最大值";
      }

      return "";
    },
    labelError() {
      if (this.codingType !== "label") return "";

      if (this.labels.some((label) => !label.description.trim())) {
        return "每個標籤的說明都必須填寫";
      }

      return "";
    },
    criterionError() {
      if (this.isParsing) return "題目檔案仍在讀取中";
      if (this.errorMessage) return "請先修正題目檔案的錯誤";
      if (!this.items.length) return "請先匯入題目";

      if (!this.criterionName.trim()) {
        return "請填寫準則庫名稱";
      }

      if (this.criterionName.trim().length > 128) {
        return "準則庫名稱不可超過 128 個字元";
      }

      if (this.codingType === "label") {
        if (this.labels.length === 0 || this.labels.length > 26) {
          return "標籤數量必須介於 1 到 26 個";
        }

        return this.labelError;
      }

      if (this.codingType !== "scale" && this.codingType !== "score") {
        return "請選擇有效的編碼方式";
      }

      if (this.rangeError) return this.rangeError;

      if (!this.scale.minDef.trim() || !this.scale.maxDef.trim()) {
        return "請填寫最小值與最大值的定義";
      }

      return "";
    },
    middlePoints() {
      if (this.codingType !== "scale" || this.rangeError) {
        return [];
      }

      const points = [];

      for (let point = this.scale.min + 1; point < this.scale.max; point += 1) {
        points.push(point);
      }

      return points;
    },
  },
};
</script>

<style scoped>
.import-error {
  white-space: pre-line;
}
</style>
